/**
 * Hook for fetching and managing clients
 */

import { useState, useEffect, useCallback } from 'react';
import type { Client, ClientFormData } from '../types/management';
import * as api from '../lib/management-api';
import { useAuth } from '../components/Auth';

interface UseClientsResult {
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  refetch: () => void;
  createClient: (data: ClientFormData) => Promise<Client>;
  updateClient: (id: string, data: ClientFormData) => Promise<Client>;
  deleteClient: (id: string) => Promise<void>;
}

/**
 * Hook to fetch and manage clients
 *
 * @returns Object with clients data, loading state, error, and CRUD operations
 */
export function useClients(): UseClientsResult {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setError('Authentication required');
      return;
    }

    const abortController = new AbortController();

    async function loadClients() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader) {
          throw new Error('No authentication token available');
        }

        const data = await api.fetchClients(
          { Authorization: authHeader },
          abortController.signal,
        );
        setClients(data);
        setHasFetched(true);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Ignore abort errors
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch clients',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadClients();

    return () => {
      abortController.abort();
    };
  }, [isAuthenticated, getAuthHeader, fetchTrigger]);

  const createClient = useCallback(
    async (data: ClientFormData): Promise<Client> => {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        throw new Error('Not authenticated');
      }

      const client = await api.createClient(data, {
        Authorization: authHeader,
      });
      setClients((prev) =>
        [...prev, client].sort((a, b) => a.name.localeCompare(b.name)),
      );
      return client;
    },
    [getAuthHeader],
  );

  const updateClient = useCallback(
    async (id: string, data: ClientFormData): Promise<Client> => {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        throw new Error('Not authenticated');
      }

      const client = await api.updateClient(id, data, {
        Authorization: authHeader,
      });
      setClients((prev) =>
        prev
          .map((c) => (c.id === id ? client : c))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      return client;
    },
    [getAuthHeader],
  );

  const deleteClient = useCallback(
    async (id: string): Promise<void> => {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        throw new Error('Not authenticated');
      }

      await api.deleteClient(id, { Authorization: authHeader });
      setClients((prev) => prev.filter((c) => c.id !== id));
    },
    [getAuthHeader],
  );

  return {
    clients,
    isLoading,
    error,
    hasFetched,
    refetch,
    createClient,
    updateClient,
    deleteClient,
  };
}
